import { Component, Input, OnInit, ElementRef, Output, EventEmitter, OnDestroy, inject, ViewChild, TemplateRef, ViewContainerRef } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { SAP_ProductStates } from 'src/assets/data/sap_product_states';
import { Subscription } from 'rxjs';
import { ToastService } from 'src/app/services/toast.service';
import { Guid } from 'guid-typescript';
import { AssemblyOrderService } from 'src/app/services/assembly-order.service';
import { MatFabButton } from '@angular/material/button';
import { NgStyle } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { Overlay, OverlayRef, ConnectedPosition } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

@Component({
    selector: 'app-state-switcher',
    templateUrl: './state-switcher.component.html',
    styleUrls: ['./state-switcher.component.scss'],
    animations: [
        trigger('fadeInOut', [
            state('void', style({
                opacity: 0
            })),
            transition('void <=> *', animate(300)),
        ])
    ],
    imports: [MatFabButton, NgStyle, MatIcon]
})
export class StateSwitcherComponent implements OnInit, OnDestroy {
  private readonly _elementRef = inject(ElementRef);
  private readonly toastr = inject(ToastService);
  private readonly assemblySvc = inject(AssemblyOrderService);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);

  @ViewChild('popupTemplate') popupTemplate!: TemplateRef<unknown>;

  @Input() disabled: boolean = false;
  @Input() itemId?: Guid;
  @Input() states: any[] = SAP_ProductStates;
  @Input() startingState: number | undefined;
  @Input() description?: string;
  @Input() onStateChangedFunction?: ((itemId: Guid, statusId: number) => Promise<boolean>);
  @Output() statusChangedResult = new EventEmitter<number>();

  private subscription?: Subscription;
  showPopup = false;
  private overlayRef?: OverlayRef;
  // Se usa CDK Overlay (en vez del popup posicionado con CSS que había antes)
  // porque el popup se abre hacia un costado del botón y, al estar anidado
  // dentro de las filas del calendario, quedaba recortado por contenedores
  // ancestros con overflow:hidden/auto (incluida una regla global de toda
  // la app). El overlay de CDK se adjunta directamente al <body>, fuera de
  // ese árbol, así que ningún overflow de un ancestro puede taparlo. Además
  // resuelve solo si conviene abrir a la izquierda o a la derecha según el
  // espacio disponible real (withPositions prueba ambas y elige la que entra
  // en pantalla), reemplazando el cálculo manual que había antes.
  popupDirection: 'left' | 'right' = 'right';
  currentState: number = 0;
  nextState: number = 1;

  ngOnInit(): void {

    this.subscription = this.assemblySvc.getPopupState().subscribe(() => {
      this.closePopup();
    });

    if (this.states && this.states.length > 0) {
      if (this.startingState !== undefined && this.states.some(state => state.id === this.startingState)) {
        this.currentState = this.states.findIndex(state => state.id === this.startingState);
      } else {
        this.currentState = 0;
      }
      this.nextState = this.findNextEnabledState(this.currentState);
    }
  }

  get currentColor() {
    return this.states[this.currentState]?.color;
  }

  get currentIcon() {
    return this.states[this.currentState]?.icon;
  }

  get currentDescription() {
    return this.states[this.currentState]?.description;
  }

  get nextColor() {
    return this.states[this.nextState]?.color;
  }

  get nextIcon() {
    return this.states[this.nextState]?.icon;
  }

  get nextDescription() {
    return this.states[this.nextState]?.description;
  }

  onChangeStatusRequested() {
    if (this.overlayRef) {
      this.closePopup();
      return;
    }

    this.assemblySvc.notifyPopupOpen(); //notify popup opened in order to close other components popups (if some remains opened).
    this.openPopup();
  }

  private openPopup(): void {
    this.showPopup = true;

    const positions: ConnectedPosition[] = [
      { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 15 },
      { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -15 },
    ];

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this._elementRef)
      .withPositions(positions)
      .withPush(false);

    positionStrategy.positionChanges.subscribe((change) => {
      this.popupDirection = change.connectionPair.originX === 'end' ? 'right' : 'left';
    });

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    const portal = new TemplatePortal(this.popupTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);

    this.overlayRef.outsidePointerEvents().subscribe((event) => {
      if (!this._elementRef.nativeElement.contains(event.target as Node)) {
        this.closePopup();
      }
    });
  }

  private closePopup(): void {
    this.showPopup = false;

    this.overlayRef?.dispose();
    this.overlayRef = undefined;
  }

  async switchState() {
    let resp = true;
    if(this.onStateChangedFunction && this.itemId) {
      resp = await this.onStateChangedFunction(this.itemId, this.states[this.nextState].id);
      if(!resp) {
        this.toastr.error(
          'Error',
          'Se produjo un error al cambiar el estado... Reintente.'
        );
        return;
      }
    }

    this.currentState = this.findNextEnabledState(this.currentState);
    this.nextState = this.findNextEnabledState(this.currentState);
    this.statusChangedResult.emit(this.states[this.currentState].id);
    this.closePopup();
  }

  findNextEnabledState(startIndex: number): number {
    let potentialNextState = (startIndex + 1) % this.states.length;

    while (!this.states[potentialNextState]?.enabled && potentialNextState !== startIndex) {
      potentialNextState = (potentialNextState + 1) % this.states.length;
    }

    // If we find an enabled state that is not the current state, return it
    if (this.states[potentialNextState]?.enabled && potentialNextState !== startIndex) {
      return potentialNextState;
    }

    // If no enabled state is found after looping through, return the original startIndex
    return startIndex;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.closePopup();
  }
}
